import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { SideDrawer, Button, DateTimePicker } from '@/components';
import {
  getCRMContacts,
  createCRMContactTask,
  updateCRMContactTask,
} from '../api';
import { getCompanyEmployees } from '../../api';
import { parseApiError } from '@/common/utils';

export function CRMTaskModal({
  isOpen,
  onClose,
  initialData = null,
  companyId,
  contactId: propContactId = null,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const isEdit = !!initialData;

  // Contact Selection
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);

  // Form Fields
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState(null);
  const [assignedToId, setAssignedToId] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [debouncedAssigneeSearch, setDebouncedAssigneeSearch] = useState('');
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);

  // Sync initialData when modal opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedContact(null);
      setContactSearch('');
      setTitle('');
      setDueAt(null);
      setAssignedToId('');
      setAssigneeSearch('');
      setIsContactDropdownOpen(false);
      setIsAssigneeDropdownOpen(false);
      return;
    }

    if (initialData) {
      const task = initialData.data || initialData;
      const contact = initialData.contact || null;
      if (contact) {
        setSelectedContact(contact);
      }
      setTitle(task.title || '');
      if (task.due_at) {
        const d = new Date(task.due_at);
        setDueAt(isNaN(d.getTime()) ? null : d);
      } else {
        setDueAt(null);
      }
      const empId = task.assigned_to_employee_id || task.assigned_to_id;
      setAssignedToId(empId ? String(empId) : '');
      const empName = task.assigned_to_employee_name || task.assigned_to_name;
      setAssigneeSearch(empName || '');
    } else {
      setSelectedContact(null);
      setContactSearch('');
      setTitle('');
      setDueAt(null);
      setAssignedToId('');
      setAssigneeSearch('');
    }
  }, [isOpen, initialData]);

  // Debounce contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearch.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  // Debounce assignee search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAssigneeSearch(assigneeSearch.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [assigneeSearch]);

  // Fetch contacts for picker when creating a task without fixed contactId
  const shouldFetchContacts =
    isOpen && !propContactId && (!initialData || !initialData.contact);

  const { data: contactsData, isLoading: isContactsLoading } = useQuery({
    queryKey: ['crm-modal-contacts', companyId, debouncedContactSearch],
    queryFn: () =>
      getCRMContacts(companyId, {
        search: debouncedContactSearch,
        page_size: 20,
      }),
    enabled: !!companyId && shouldFetchContacts,
  });

  const contactsList = contactsData?.results || [];

  // Fetch employees for assignee picker
  const { data: employeesData, isLoading: isEmployeesLoading } = useQuery({
    queryKey: ['crm-modal-employees', companyId, debouncedAssigneeSearch],
    queryFn: () =>
      getCompanyEmployees(companyId, {
        search: debouncedAssigneeSearch,
        page_size: 50,
      }),
    enabled: !!companyId && isOpen,
  });

  const employeesList =
    employeesData?.results ||
    (Array.isArray(employeesData) ? employeesData : []);

  // Mutation: Create Task
  const createMutation = useMutation({
    mutationFn: ({ contactId, payload }) =>
      createCRMContactTask({ companyId, contactId, payload }),
    onSuccess: () => {
      toast.success('Task added successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add task.');
    },
  });

  // Mutation: Update Task
  const updateMutation = useMutation({
    mutationFn: ({ contactId, taskId, payload }) =>
      updateCRMContactTask({ companyId, contactId, taskId, payload }),
    onSuccess: () => {
      toast.success('Task updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update task.');
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e) => {
    e?.preventDefault?.();

    const targetContactId =
      propContactId ||
      selectedContact?.id ||
      initialData?.contact?.id ||
      initialData?.data?.contact_id;

    if (!targetContactId) {
      toast.error('Please select a contact / lead.');
      return;
    }

    if (!title.trim()) {
      toast.error('Please enter a task title.');
      return;
    }

    const payload = {
      title: title.trim(),
      due_at: dueAt ? dueAt.toISOString() : null,
      assigned_to_id: assignedToId ? parseInt(assignedToId, 10) : null,
      assigned_to_employee_id: assignedToId ? parseInt(assignedToId, 10) : null,
    };

    if (isEdit) {
      const taskId = initialData.data?.id || initialData.id;
      updateMutation.mutate({
        contactId: targetContactId,
        taskId,
        payload,
      });
    } else {
      createMutation.mutate({
        contactId: targetContactId,
        payload,
      });
    }
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Task' : 'Add New Task'}
      width="lg"
      className="crm-task-drawer"
      headerClassName="!px-6 !py-4 sm:!py-5"
      bodyClassName="box-border !px-6 !py-5 overflow-y-auto"
      footerClassName="!px-6"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isPending}
            className="h-10 px-4 text-sm"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={isPending}
            className="h-10 px-5 text-sm font-semibold"
          >
            {isPending
              ? isEdit
                ? 'Saving...'
                : 'Adding...'
              : isEdit
                ? 'Save Changes'
                : 'Add Task'}
          </Button>
        </div>
      }
    >
      <form
        id="crm-task-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 pt-1 font-sf-pro-display"
      >
        {/* Contact Selector (only when contact is not pre-fixed) */}
        {!propContactId && (
          <div className="flex flex-col gap-1.5 relative">
            <label className="text-sm font-medium text-text-primary">
              Contact / Lead <span className="text-red-500">*</span>
            </label>
            {selectedContact ? (
              <div className="flex items-center justify-between p-3 rounded-[10px] bg-[#EBF4EA]/50 border border-[#50AB3D]/40">
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-[#1F2241] truncate">
                    {selectedContact.name || 'Unnamed Contact'}
                  </span>
                  {selectedContact.phone && (
                    <span className="text-xs text-text-secondary">
                      {selectedContact.phone}
                    </span>
                  )}
                </div>
                {!isEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedContact(null);
                      setContactSearch('');
                    }}
                    className="text-xs font-semibold text-[#50AB3D] hover:underline shrink-0 ml-2"
                  >
                    Change
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    setIsContactDropdownOpen(true);
                  }}
                  onFocus={() => setIsContactDropdownOpen(true)}
                  placeholder="Search contact by name or phone..."
                  className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors"
                />
                {isContactDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsContactDropdownOpen(false)}
                    />
                    <div className="absolute left-0 right-0 top-full mt-1.5 max-h-48 overflow-y-auto bg-white border border-[#EBF4EA] rounded-xl shadow-lg z-50 py-1 divide-y divide-gray-100">
                      {isContactsLoading ? (
                        <div className="p-3 text-xs text-center text-text-muted">
                          Loading contacts...
                        </div>
                      ) : contactsList.length === 0 ? (
                        <div className="p-3 text-xs text-center text-text-muted">
                          No contacts found
                        </div>
                      ) : (
                        contactsList.map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => {
                              setSelectedContact(contact);
                              setIsContactDropdownOpen(false);
                            }}
                            className="px-4 py-2.5 hover:bg-[#EBF4EA]/40 cursor-pointer transition-colors flex flex-col"
                          >
                            <span className="text-sm font-medium text-[#1F2241]">
                              {contact.name || 'Unnamed'}
                            </span>
                            {contact.phone && (
                              <span className="text-xs text-text-secondary">
                                {contact.phone}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Task Title */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text-primary">
            Task Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter task title..."
            className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors"
            disabled={isPending}
          />
        </div>

        {/* Due Date & Time */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-text-primary">
            Due Date & Time
          </label>
          <DateTimePicker
            selected={dueAt}
            onChange={(date) => setDueAt(date)}
            mode="datetime"
            allowPastDates={false}
            disablePastTime={true}
            placeholderText="Select due date & time"
            disabled={isPending}
            className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors"
          />
        </div>

        {/* Assign User */}
        <div className="flex flex-col gap-1.5 relative">
          <label className="text-sm font-medium text-text-primary">
            Assign User
          </label>
          <div className="relative">
            <input
              type="text"
              value={assigneeSearch}
              onChange={(e) => {
                setAssigneeSearch(e.target.value);
                setIsAssigneeDropdownOpen(true);
              }}
              onFocus={() => setIsAssigneeDropdownOpen(true)}
              placeholder="Search employee by name..."
              className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors"
              disabled={isPending}
            />
            {assignedToId && (
              <button
                type="button"
                onClick={() => {
                  setAssignedToId('');
                  setAssigneeSearch('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted hover:text-red-500"
              >
                Clear
              </button>
            )}

            {isAssigneeDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsAssigneeDropdownOpen(false)}
                />
                <div className="absolute left-0 right-0 top-full mt-1.5 max-h-48 overflow-y-auto bg-white border border-[#EBF4EA] rounded-xl shadow-lg z-50 py-1 divide-y divide-gray-100">
                  {isEmployeesLoading ? (
                    <div className="p-3 text-xs text-center text-text-muted">
                      Loading employees...
                    </div>
                  ) : employeesList.length === 0 ? (
                    <div className="p-3 text-xs text-center text-text-muted">
                      No employees found
                    </div>
                  ) : (
                    employeesList.map((emp) => {
                      const empName =
                        emp.name ||
                        emp.username ||
                        (emp.first_name
                          ? `${emp.first_name} ${emp.last_name || ''}`.trim()
                          : 'Unnamed');
                      const roleName =
                        typeof emp.role === 'object' && emp.role !== null
                          ? emp.role?.name || ''
                          : typeof emp.role === 'string'
                            ? emp.role
                            : '';

                      return (
                        <div
                          key={emp.id}
                          onClick={() => {
                            setAssignedToId(String(emp.id));
                            setAssigneeSearch(empName);
                            setIsAssigneeDropdownOpen(false);
                          }}
                          className={`px-4 py-2.5 hover:bg-[#EBF4EA]/40 cursor-pointer transition-colors flex items-center justify-between ${
                            String(assignedToId) === String(emp.id)
                              ? 'bg-[#EBF4EA]/60 font-semibold text-[#50AB3D]'
                              : 'text-[#1F2241]'
                          }`}
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm">{empName}</span>
                            {emp.email && (
                              <span className="text-xs text-text-muted truncate">
                                {emp.email}
                              </span>
                            )}
                          </div>
                          {roleName && (
                            <span className="text-xs text-text-muted shrink-0 ml-2">
                              {roleName}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </form>
    </SideDrawer>
  );
}
